// hooks/useAlert.ts
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export type AlertType = 'success' | 'danger' | 'warning' | 'info';

export interface AlertConfig {
    message: string;
    type?: AlertType;
    duration?: number;
    autoHide?: boolean;
}

interface Alert extends Required<AlertConfig> {
    id: string;
}

interface UseAlertConfig {
    fromLocation?: boolean;
    defaultDuration?: number;
    defaultType?: AlertType;
    autoHide?: boolean;
}

interface UseAlertReturn {
    alert: Alert | null;
    showAlert: (config: AlertConfig) => void;
    hideAlert: () => void;
    isVisible: boolean;
    clearAlert: () => void; // Alias for hideAlert
}

export const useAlert = (config: UseAlertConfig = {}): UseAlertReturn => {
    const {
        fromLocation = false,
        defaultDuration = 10000, // 10 seconds
        defaultType = 'success',
        autoHide = true
    } = config;

    const location = useLocation();
    const [alert, setAlert] = useState<Alert | null>(null);

    // Generate unique ID for each alert
    const generateAlertId = () => `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Initialize alert from location state when component mounts or location changes
    useEffect(() => {
        if (fromLocation && location.state?.message) {
            const locationAlert: Alert = {
                id: generateAlertId(),
                message: location.state.message,
                type: location.state.type || defaultType,
                duration: location.state.duration || defaultDuration,
                autoHide: location.state.autoHide ?? autoHide
            };

            setAlert(locationAlert);

            // Clear location state to prevent showing alert again on refresh
            // This is optional - you might want to keep it depending on your needs
            /*if (window.history.replaceState) {
                const newState = { ...location.state };
                delete newState.message;
                delete newState.type;
                delete newState.duration;
                delete newState.autoHide;
                window.history.replaceState(newState, '', location.pathname);
            }
            location.state.message = null;*/
        }
    }, [location.state, fromLocation, defaultType, defaultDuration, autoHide, location.pathname]);

    // Auto-hide timer effect
    useEffect(() => {
        if (!alert || !alert.autoHide || alert.duration === 0) {
            return;
        }

        const timer = setTimeout(() => {
            setAlert(null);
        }, alert.duration);

        // Cleanup timer on component unmount or alert change
        return () => clearTimeout(timer);
    }, [alert]);

    // Show alert function
    const showAlert = useCallback((alertConfig: AlertConfig) => {
        const newAlert: Alert = {
            id: generateAlertId(),
            message: alertConfig.message,
            type: alertConfig.type || defaultType,
            duration: alertConfig.duration || defaultDuration,
            autoHide: alertConfig.autoHide ?? autoHide
        };

        setAlert(newAlert);
    }, [defaultType, defaultDuration, autoHide]);

    // Hide alert function
    const hideAlert = useCallback(() => {
        setAlert(null);
    }, []);

    // Clear alert (alias for hideAlert for semantic clarity)
    const clearAlert = hideAlert;

    return {
        alert,
        showAlert,
        hideAlert,
        isVisible: !!alert,
        clearAlert
    };
};
